import { useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useAuth, useAuthActions } from '@/hooks/use-auth'
import { ApiError } from '@/lib/api'

/**
 * Login page.
 *
 * Mobile-first: large field labels, full-width primary CTA at
 * 56px height, error message below the button (so the keyboard
 * doesn't obscure it on iOS).
 *
 * No "forgot password" or "sign up" links — the V2 product
 * decision is that admins provision driver accounts, and password
 * resets go through admin tooling. We'll add a self-serve flow
 * once it's actually justified.
 */
export function LoginPage() {
  const navigate = useNavigate()
  const { isAuthenticated, hydrated } = useAuth()
  const { login } = useAuthActions()
  // `redirect` lets us send a half-authenticated user back to
  // wherever they tried to go before being bounced to login.
  const search = useSearch({ strict: false }) as { redirect?: string }

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // If the user is already logged in (e.g. they typed /login on a
  // session that hydrated from localStorage), shortcut to the app.
  useEffect(() => {
    if (hydrated && isAuthenticated) {
      void navigate({ to: search.redirect ?? '/dashboard' })
    }
  }, [hydrated, isAuthenticated, navigate, search.redirect])

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(email, password)
      await navigate({ to: search.redirect ?? '/dashboard' })
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        // Network error, JSON parse error, etc.
        setError('Connexion impossible. Vérifiez votre connexion réseau.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-surface-muted">
      {/* Brand mark — visible focal point for the page. */}
      <div className="mb-8 text-center">
        <div
          aria-hidden="true"
          className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand text-brand-fg text-2xl font-bold shadow-md"
        >
          A
        </div>
        <h1 className="mt-3 text-2xl font-bold text-text">Avity</h1>
        <p className="text-sm text-text-muted">Application chauffeur</p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm flex flex-col gap-4 bg-surface rounded-2xl p-6 shadow-sm border border-surface-border"
        // Each field below carries its own autocomplete hint so iOS
        // / Android password managers can fill the form correctly.
        noValidate
      >
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
        />
        <Input
          label="Mot de passe"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={submitting}
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={submitting}
          disabled={submitting || email === '' || password === ''}
        >
          {submitting ? <Spinner label="Connexion en cours" /> : 'Se connecter'}
        </Button>

        {error !== null ? (
          <p
            role="alert"
            className="text-center text-sm text-danger font-medium mt-1"
          >
            {error}
          </p>
        ) : null}
      </form>

      <p className="mt-6 text-xs text-text-muted text-center max-w-xs">
        Vos identifiants vous ont été communiqués par votre administrateur.
      </p>
    </div>
  )
}
